import React, { useMemo } from 'react';
import {
  SimulationHotspotInteraction,
  SimulationSceneInteraction,
} from '../../../types/v2';

interface WindowsEvidenceSceneProps {
  renderState: Record<string, any>;
  interactions?: SimulationSceneInteraction[];
  disabled?: boolean;
  onAction: (actionId: string) => void;
}

export const WindowsEvidenceScene: React.FC<WindowsEvidenceSceneProps> = ({
  renderState,
  interactions = [],
  disabled = false,
  onAction,
}) => {
  const hotspots = useMemo(() => Object.fromEntries(
    interactions
      .filter((item): item is SimulationHotspotInteraction => item.kind === 'hotspot')
      .map((item) => [item.id, item]),
  ), [interactions]);

  const submit = (id: string) => {
    const interaction = hotspots[id];
    if (!interaction || disabled) return;
    onAction(interaction.action_id);
  };

  const screenMode = String(renderState.screen_mode || 'desktop');
  const memoryHigh = renderState.memory_level === 'high';
  const manyTabs = renderState.browser_load === 'many_tabs';
  const keyboardWorking = renderState.keyboard_response === 'working';
  const keyboardPort = renderState.keyboard_port === 'usb_b' ? 'USB Port B' : 'USB Port A';

  return (
    <div className="rounded-xl border border-slate-300 bg-slate-100 p-3" aria-label="Interactive Windows troubleshooting workstation">
      <div className="overflow-hidden rounded-lg border border-slate-400 bg-white">
        <div className="flex items-center justify-between border-b border-slate-300 bg-slate-800 px-3 py-2 text-xs text-white">
          <span>Windows Workstation</span>
          <span>{screenMode === 'desktop' ? 'Desktop' : screenMode === 'task_manager' ? 'Task Manager' : 'Device Manager'}</span>
        </div>

        {screenMode === 'desktop' ? (
          <div className="min-h-[220px] bg-sky-100 p-5">
            <div className="rounded-lg border border-slate-300 bg-white/95 p-4">
              <p className="text-sm font-semibold text-slate-900">User report</p>
              {Object.prototype.hasOwnProperty.call(renderState, 'memory_level') ? (
                <p className="mt-2 text-sm text-slate-700">The computer starts normally, but it feels very slow once the user begins working.</p>
              ) : (
                <p className="mt-2 text-sm text-slate-700">The USB keyboard stopped responding while Windows continued running normally.</p>
              )}
              {Object.prototype.hasOwnProperty.call(renderState, 'keyboard_response') ? (
                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  Keyboard: <strong>{keyboardWorking ? 'responding' : 'not responding'}</strong> · Connected to <strong>{keyboardPort}</strong>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {screenMode === 'task_manager' ? (
          <div className="min-h-[220px] p-4">
            <div className="mb-3 flex gap-4 border-b border-slate-200 pb-2 text-sm font-medium text-slate-700">
              <span>Processes</span><span className="border-b-2 border-slate-700">Performance</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded border border-slate-200 p-3"><div className="text-xs text-slate-500">CPU</div><div className="text-xl font-semibold">18%</div></div>
              <div className="rounded border border-slate-200 p-3"><div className="text-xs text-slate-500">Memory</div><div className="text-xl font-semibold">{memoryHigh ? '92%' : '61%'}</div></div>
              <div className="rounded border border-slate-200 p-3"><div className="text-xs text-slate-500">Disk</div><div className="text-xl font-semibold">12%</div></div>
            </div>
            <div className="mt-4 rounded border border-slate-200 p-3 text-sm text-slate-700">
              Browser workload: <strong>{manyTabs ? 'many open tabs; largest current memory user' : 'unneeded tabs closed; memory use reduced'}</strong>
            </div>
          </div>
        ) : null}

        {screenMode === 'device_manager' ? (
          <div className="min-h-[220px] p-4">
            <p className="mb-3 text-sm font-semibold text-slate-900">Device Manager</p>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
              <div>▾ Keyboards</div>
              <div className="ml-5 mt-2">⌨ HID Keyboard Device</div>
              <div className="ml-5 mt-1 text-xs text-slate-500">No warning icon shown</div>
            </div>
            <div className="mt-4 rounded border border-slate-200 p-3 text-sm text-slate-700">
              Physical check: keyboard is connected to <strong>{keyboardPort}</strong> and is <strong>{keyboardWorking ? 'responding' : 'not responding'}</strong>.
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {hotspots.inspect_desktop ? <button type="button" disabled={disabled} onClick={() => submit('inspect_desktop')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Inspect symptom</button> : null}
        {hotspots.task_manager ? <button type="button" disabled={disabled} onClick={() => submit('task_manager')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Open Task Manager</button> : null}
        {hotspots.performance ? <button type="button" disabled={disabled} onClick={() => submit('performance')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Inspect performance</button> : null}
        {hotspots.close_tabs ? <button type="button" disabled={disabled} onClick={() => submit('close_tabs')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Close unneeded tabs</button> : null}
        {hotspots.device_manager ? <button type="button" disabled={disabled} onClick={() => submit('device_manager')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Open Device Manager</button> : null}
        {hotspots.inspect_keyboard ? <button type="button" disabled={disabled} onClick={() => submit('inspect_keyboard')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Inspect keyboard listing</button> : null}
        {hotspots.other_usb_port ? <button type="button" disabled={disabled} onClick={() => submit('other_usb_port')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Test known-good USB port</button> : null}
        {hotspots.verify ? <button type="button" disabled={disabled} onClick={() => submit('verify')} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">Verify result</button> : null}
      </div>
    </div>
  );
};
